/**
 * Tinte der Unterschrift (SICHT-OEFFENTLICH-BETRIEB-15, SICHT-CREATOR-23): Verträge, PDF und Ausdruck zeigen die Unterschrift
 * auf weißem Papier. Eine Unterschrift muss deshalb immer mit dunkler Tinte vorliegen. Ältere Profil-Unterschriften der
 * Creator wurden im Dunkelmodus mit der Textfarbe (fast weiß) auf transparentem Grund gezeichnet; sie werden beim
 * Übernehmen und beim Ausfertigen auf die feste Tinte umgefärbt. Eingescannte Bilder mit eigenem (weißem) Grund bleiben
 * unverändert.
 */

/** Farbe der gezeichneten Unterschrift; die Zeichenflächen sind immer weiß. */
export const SIGNATURE_INK = "#1b1f1d";
const INK_RGB = [0x1b, 0x1f, 0x1d] as const;

/** Anteil durchsichtiger Pixel, ab dem ein Bild als „Tinte auf transparentem Grund“ gilt. */
const MIN_TRANSPARENT = 0.2;
/** Helligkeit (0…1) der Tinte, ab der sie auf Weiß nicht mehr lesbar ist (Kontrast unter etwa 2:1). */
const LIGHT_INK = 0.6;

/** Relative Helligkeit eines sRGB-Pixels (0…1). */
function luminance(r: number, g: number, b: number): number {
  const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * true, wenn die RGBA-Pixel eine helle Tinte auf durchsichtigem Grund zeigen (auf Weiß praktisch unsichtbar). Gewichtet
 * nach Deckkraft; Bilder mit eigenem, deckendem Grund (Scan, JPG) sind nie betroffen.
 */
export function hasLightInk(px: Uint8ClampedArray | Uint8Array): boolean {
  const total = px.length / 4;
  if (!total) return false;
  let transparent = 0, weight = 0, sum = 0;
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3]!;
    if (a === 0) { transparent++; continue; }
    const w = a / 255;
    weight += w;
    sum += w * luminance(px[i]!, px[i + 1]!, px[i + 2]!);
  }
  if (!weight || transparent / total < MIN_TRANSPARENT) return false;
  return sum / weight > LIGHT_INK;
}

/** Färbt jede sichtbare Tinte (Deckkraft bleibt) auf die feste Unterschriftsfarbe um; ändert das Feld selbst. */
export function recolorInk(px: Uint8ClampedArray | Uint8Array): void {
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] === 0) continue;
    px[i] = INK_RGB[0];
    px[i + 1] = INK_RGB[1];
    px[i + 2] = INK_RGB[2];
  }
}

/** Zeichenfläche mit dem Bild; helle Tinte ist bereits umgefärbt (changed). Nur im Browser, sonst null. */
export async function inkCanvas(dataUrl: string): Promise<{ canvas: HTMLCanvasElement; changed: boolean } | null> {
  if (typeof document === "undefined") return null;
  try {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, img.naturalWidth);
    canvas.height = Math.max(1, img.naturalHeight);
    const g = canvas.getContext("2d");
    if (!g) return null;
    g.drawImage(img, 0, 0);
    const data = g.getImageData(0, 0, canvas.width, canvas.height);
    if (!hasLightInk(data.data)) return { canvas, changed: false };
    recolorInk(data.data);
    g.putImageData(data, 0, 0);
    return { canvas, changed: true };
  } catch {
    return null;
  }
}

/** PNG-data-URL mit lesbarer Tinte: unverändert (gleiche Bytes), wenn nichts umzufärben ist oder der Browser es nicht kann. */
export async function withDarkInk(dataUrl: string): Promise<string> {
  if (!/^data:image\/png;base64,/.test(dataUrl)) return dataUrl;
  const k = await inkCanvas(dataUrl);
  return k?.changed ? k.canvas.toDataURL("image/png") : dataUrl;
}

/** PNG-Datei als data-URL (ohne Größenprüfung). */
export async function pngBlobToDataUrl(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return `data:image/png;base64,${btoa(bin)}`;
}
